import { __awaiter } from "tslib";
export const FORCED_DECISION_NULL_RULE_KEY = '$opt_null_rule_key';
export default class OptimizelyUserContext {
    constructor({ optimizely, userId, attributes }) {
        this._qualifiedSegments = null;
        this.optimizely = optimizely;
        this.userId = userId;
        this.attributes = Object.assign({}, attributes);
        this.forcedDecisionsMap = {};
    }
    /**
     * Sets an attribute for a given key.
     * @param     {string}                     key         An attribute key
     * @param     {any}                        value       An attribute value
     */
    setAttribute(key, value) {
        this.attributes[key] = value;
    }
    getUserId() {
        return this.userId;
    }
    getAttributes() {
        return Object.assign({}, this.attributes);
    }
    getOptimizely() {
        return this.optimizely;
    }
    get qualifiedSegments() {
        return this._qualifiedSegments;
    }
    set qualifiedSegments(qualifiedSegments) {
        this._qualifiedSegments = qualifiedSegments;
    }
    /**
     * Returns a decision result for a given flag key and a user context, which contains all data required to deliver the flag.
     * If the SDK finds an error, it will return a decision with null for variationKey. The decision will include an error message in reasons.
     * @param     {string}                     key         A flag key for which a decision will be made.
     * @param     {OptimizelyDecideOption}     options     An array of options for decision-making.
     * @return    {OptimizelyDecision}                     A decision result.
     */
    decide(key, options = []) {
        return this.optimizely.decide(this.cloneUserContext(), key, options);
    }
    /**
     * Returns a promise that resolves in decision result for a given flag key and a user context, which contains all data required to deliver the flag.
     * If the SDK finds an error, it will return a decision with null for variationKey. The decision will include an error message in reasons.
     * @param     {string}                     key         A flag key for which a decision will be made.
     * @param     {OptimizelyDecideOption}     options     An array of options for decision-making.
     * @return    {Promise<OptimizelyDecision>}                     A Promise that resolves decision result.
     */
    decideAsync(key, options) {
        return this.optimizely.decideAsync(this.cloneUserContext(), key, options);
    }
    /**
     * Returns an object of decision results for multiple flag keys and a user context.
     * If the SDK finds an error for a key, the response will include a decision for the key showing reasons for the error.
     * The SDK will always return key-mapped decisions. When it cannot process requests, it will return an empty map after logging the errors.
     * @param     {string[]}                   keys        An array of flag keys for which decisions will be made.
     * @param     {OptimizelyDecideOption[]}   options     An array of options for decision-making.
     * @return    {[key: string]: OptimizelyDecision}      An object of decision results mapped by flag keys.
     */
    decideForKeys(keys, options = []) {
        return this.optimizely.decideForKeys(this.cloneUserContext(), keys, options);
    }
    /**
     * Returns a promise that resolves in an object of decision results for multiple flag keys and a user context.
     * If the SDK finds an error for a key, the response will include a decision for the key showing reasons for the error.
     * The SDK will always return key-mapped decisions. When it cannot process requests, it will return an empty map after logging the errors.
     * @param     {string[]}                   keys        An array of flag keys for which decisions will be made.
     * @param     {OptimizelyDecideOption[]}   options     An array of options for decision-making.
     * @return    {Promise<Record<string, OptimizelyDecision>>}      A promise that resolves in an object of decision results mapped by flag keys.
     */
    decideForKeysAsync(keys, options) {
        return this.optimizely.decideForKeysAsync(this.cloneUserContext(), keys, options);
    }
    /**
     * Returns an object of decision results for all active flag keys.
     * @param     {OptimizelyDecideOption[]}   options     An array of options for decision-making.
     * @return    {[key: string]: OptimizelyDecision}      An object of all decision results mapped by flag keys.
     */
    decideAll(options = []) {
        return this.optimizely.decideAll(this.cloneUserContext(), options);
    }
    /**
     * Returns a promise that resolves in an object of decision results for all active flag keys.
     * @param     {OptimizelyDecideOption[]}   options     An array of options for decision-making.
     * @return    {Promise<Record<string ,OptimizelyDecision>>}      A promise that resolves in an object of all decision results mapped by flag keys.
     */
    decideAllAsync(options = []) {
        return this.optimizely.decideAllAsync(this.cloneUserContext(), options);
    }
    /**
     * Tracks an event.
     * @param     {string}                     eventName The event name.
     * @param     {EventTags}                  eventTags An optional map of event tag names to event tag values.
     */
    trackEvent(eventName, eventTags) {
        this.optimizely.track(eventName, this.userId, this.attributes, eventTags);
    }
    /**
     * Sets the forced decision for specified optimizely decision context.
     * @param     {OptimizelyDecisionContext}   context      OptimizelyDecisionContext containing flagKey and optional ruleKey.
     * @param     {OptimizelyForcedDecision}    decision     OptimizelyForcedDecision containing forced variation key.
     * @return    {boolean}                     true if the forced decision has been set successfully.
     */
    setForcedDecision(context, decision) {
        var _a;
        const flagKey = context.flagKey;
        const ruleKey = (_a = context.ruleKey) !== null && _a !== void 0 ? _a : FORCED_DECISION_NULL_RULE_KEY;
        const variationKey = decision.variationKey;
        const forcedDecision = { variationKey };
        if (!this.forcedDecisionsMap[flagKey]) {
            this.forcedDecisionsMap[flagKey] = {};
        }
        this.forcedDecisionsMap[flagKey][ruleKey] = forcedDecision;
        return true;
    }
    /**
     * Returns the forced decision for specified optimizely decision context.
     * @param     {OptimizelyDecisionContext}  context  OptimizelyDecisionContext containing flagKey and optional ruleKey.
     * @return    {OptimizelyForcedDecision|null}       OptimizelyForcedDecision for specified context if exists or null.
     */
    getForcedDecision(context) {
        return this.findForcedDecision(context);
    }
    /**
     * Removes the forced decision for specified optimizely decision context.
     * @param     {OptimizelyDecisionContext}  context  OptimizelyDecisionContext containing flagKey and optional ruleKey.
     * @return    {boolean}                    true if the forced decision has been removed successfully
     */
    removeForcedDecision(context) {
        var _a;
        const ruleKey = (_a = context.ruleKey) !== null && _a !== void 0 ? _a : FORCED_DECISION_NULL_RULE_KEY;
        const flagKey = context.flagKey;
        let isForcedDecisionRemoved = false;
        if (this.forcedDecisionsMap.hasOwnProperty(flagKey)) {
            const forcedDecisionByRuleKey = this.forcedDecisionsMap[flagKey];
            if (forcedDecisionByRuleKey.hasOwnProperty(ruleKey)) {
                delete this.forcedDecisionsMap[flagKey][ruleKey];
                isForcedDecisionRemoved = true;
            }
            if (Object.keys(this.forcedDecisionsMap[flagKey]).length === 0) {
                delete this.forcedDecisionsMap[flagKey];
            }
        }
        return isForcedDecisionRemoved;
    }
    /**
     * Removes all forced decisions bound to this user context.
     * @return    {boolean}                    true if the forced decision has been removed successfully
     */
    removeAllForcedDecisions() {
        this.forcedDecisionsMap = {};
        return true;
    }
    /**
     * Finds a forced decision in forcedDecisionsMap for provided optimizely decision context.
     * @param     {OptimizelyDecisionContext}     context  OptimizelyDecisionContext containing flagKey and optional ruleKey.
     * @return    {OptimizelyForcedDecision|null}          OptimizelyForcedDecision for specified context if exists or null.
     */
    findForcedDecision(context) {
        var _a;
        let variationKey;
        const validRuleKey = (_a = context.ruleKey) !== null && _a !== void 0 ? _a : FORCED_DECISION_NULL_RULE_KEY;
        const flagKey = context.flagKey;
        if (this.forcedDecisionsMap.hasOwnProperty(context.flagKey)) {
            const forcedDecisionByRuleKey = this.forcedDecisionsMap[flagKey];
            if (forcedDecisionByRuleKey.hasOwnProperty(validRuleKey)) {
                variationKey = forcedDecisionByRuleKey[validRuleKey].variationKey;
                return { variationKey };
            }
        }
        return null;
    }
    cloneUserContext() {
        const userContext = new OptimizelyUserContext({
            optimizely: this.getOptimizely(),
            userId: this.getUserId(),
            attributes: this.getAttributes(),
        });
        if (Object.keys(this.forcedDecisionsMap).length > 0) {
            userContext.forcedDecisionsMap = Object.assign({}, this.forcedDecisionsMap);
        }
        userContext._qualifiedSegments = this._qualifiedSegments;
        return userContext;
    }
    /**
     * Fetches a target user's list of qualified segments filtered by any given segment options and stores in qualifiedSegments.
     * @param {OptimizelySegmentOption[]} options   (Optional) List of segment options used to filter qualified segment results.
     * @returns Boolean representing if segments were populated.
     */
    fetchQualifiedSegments(options) {
        return __awaiter(this, void 0, void 0, function* () {
            const segments = yield this.optimizely.fetchQualifiedSegments(this.userId, options);
            this.qualifiedSegments = segments;
            return segments !== null;
        });
    }
    /**
     * Returns a boolean representing if a user is qualified for a particular segment.
     * @param   {string}  segment   Target segment to be evaluated for user qualification.
     * @returns {boolean}           Boolean representing if a user qualified for the passed in segment.
     */
    isQualifiedFor(segment) {
        if (!this._qualifiedSegments) {
            return false;
        }
        return this._qualifiedSegments.indexOf(segment) > -1;
    }
}
export const __platforms = ['__universal__'];
//# sourceMappingURL=index.js.map